import { Box, CircularProgress, Typography } from "@mui/material";
import clsx from "clsx";

import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResponsiveContainer } from "recharts";
import BriaButton from "../../../components/common/BriaButton/BriaButton.tsx";
import BriaDropDown, { DropDownItem } from "../../../components/common/BriaDropDown/BriaDropDown.tsx";
import CustomDateRangePicker from "../../../components/common/DateRangePicker/DateRangePicker.tsx";
import CustomLineChart from "../../../components/common/LineChart/LineChart.tsx";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { usePostHog } from "../../../hooks/usePostHog.tsx";
import TableLayout, { TableRow } from "../../../layout/TableLayout/TableLayout.tsx";
import {
	ITableUsageRecord,
	IUsageData,
	LineChartColors,
	LineChartDefaultValues,
	UsagePeriod,
} from "../../../models/usage.ts";
import { convertDateToUtc, getTodayDateRange } from "../../../utils";
import classes from "../../auth/ForgotPassword/ForgotPassword.module.scss";
import styles from "./UsageAndBilling.module.scss";
import {
	ACCESS_METHOD_MAPPER,
	DROPDOWN_HEIGHT,
	DROPDOWN_MAX_HEIGHT,
	DROPDOWN_WIDTH,
	ROUTES_MAPPER,
	TG_MODEL_DROPDOWN_WIDTH,
	computeTableData,
	convertUsageData,
	createModelLookup,
	defaultApiRoutesDropDownItems,
	usageTypeDropDownItems as defaultUsageTypesDropDownItems,
	filterTabularData,
	filterUsageData,
	filterUsageSummary,
	getApiRoutesAndTailoredModels,
	handleDownloadAsCsvClick,
	handleHoverRange,
	handleSelectApiRoutes,
	handleSelectUsageType,
	usagePeriodDropDownItems,
} from "./UsageAndBillingUtils.tsx";

const UsageAndBilling = () => {
	const { accountStore, appStore, tgStore, authStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const { t } = useTranslation("translation", { keyPrefix: "account.usage" });

	// Table variables
	const [tableData, setTableData] = useState<TableRow<ITableUsageRecord>[]>([]);
	const [tableHeader, setTableHeader] = useState<string[]>([]);

	// Usage data variable
	const [usageData, setUsageData] = useState<IUsageData[]>([]);
	const [selectedUsagePeriod, setSelectedUsagePeriod] = useState<UsagePeriod>(UsagePeriod.Daily);

	// Date range variables
	const [selectedDateRange, setSelectedRangeDate] = useState<Date[]>([]);
	const [dateRange, setDateRange] = useState<[Date, Date]>(getTodayDateRange());

	// Features variables
	const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
	const [featuresDropDownItems, setFeaturesDropDownItems] = useState<DropDownItem[]>(defaultApiRoutesDropDownItems);
	const [filteredFeaturesDropDownItems, setFilteredFeaturesDropDownItems] = useState<DropDownItem[]>(defaultApiRoutesDropDownItems);
	const [activeFeatures, setActiveFeatures] = useState<string[]>([]);
	const [featuresSearchTerm, setFeaturesSearchTerm] = useState<string>("")

	// Access methods variables
	const [selectedAccessMethods, setSelectedAccessMethods] = useState<string[]>([]);
	const [accessMethodsDropDownItems, setAccessMethodsDropDownItems] = useState<DropDownItem[]>(defaultUsageTypesDropDownItems);

	// Tailored models variables
	const [selectedTailoredModels, setSelectedTailoredModels] = useState<number[]>([]);
	const [tailoredModelsDropDownItems, setTailoredModelsDropDownItems] = useState<DropDownItem[]>([]);
	const [filteredTailoredModelsDropDownItems, setFilteredTailoredModelsDropDownItems] = useState<DropDownItem[]>([]);
	const [activeTailoredModels, setActiveTailoredModels] = useState<string[]>([]);
	const [tgModelSearchTerm, setTgModelSearchTerm] = useState<string>("")

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const handleUsageDataFiltering = () => {
		if (selectedDateRange.length > 0) {
			filterUsageData(
				accountStore,
				selectedFeatures,
				selectedTailoredModels,
				selectedAccessMethods.filter((ut) => ut !== t("selectAll")),
			);
			// Create a model lookup map to optimize searching
			const modelLookup = createModelLookup({ models: tgStore.tgModelStore.modelsList });
			// Convert usage data
			const computedUsageData: IUsageData[] = convertUsageData({
				usageData: accountStore.usageData,
				modelLookup,
			});
			setUsageData(computedUsageData);
			filterTabularData(accountStore, computedUsageData);
			const { data, header } = computeTableData(accountStore, tgStore.tgModelStore, t);

			setTableData(data);
			setTableHeader(header);
			filterUsageSummary(accountStore);
		}
	};

	// Loads API keys, TG models, sets initial date range
	useEffect(() => {
		const loadInitialData = async () => {
			if (!appStore.isError && appStore.apiKeys.length === 0) {
				await appStore.loadApiKeys();
			}

			try {
				if (
					!tgStore.tgModelStore.modelsListError &&
					tgStore.tgModelStore.modelsList.length === 0 &&
					!tgStore.tgModelStore.loadingModelsList
				) {
					await tgStore.tgModelStore.listAllModels();
				}
			} catch (err) {
				console.error(err);
			}

			const range = getTodayDateRange();
			setDateRange(range);
			setSelectedRangeDate(range);
		};
		loadInitialData();
	}, []);

	// frontend filtering of usage date depending on selected TG models, features and access methods
	useEffect(() => {
		if (!isLoading) {
			handleUsageDataFiltering();
		}
	}, [selectedFeatures, selectedAccessMethods, selectedTailoredModels]);

	// Loads all usage data which is loaded everytime date range is changed
	useEffect(() => {
		const loadAllUsageData = async () => {
			if (selectedDateRange.length == 0) return;
			setIsLoading(true);

			const apiRoutesItemsSorted = [...defaultApiRoutesDropDownItems].sort((a, b) =>
				a.value > b.value ? 1 : -1,
			);
			const selectAllLabel = t("selectAll");
			const apiRoutesItems = [{ key: selectAllLabel, value: selectAllLabel }, ...apiRoutesItemsSorted];
			const apiRoutesSelected = apiRoutesItems.map((item) => item.key);

			const tgModelsItems = tgStore.tgModelStore.modelsList
				.map((m) => ({ key: m.name, value: m.id }))
				.sort((a, b) => (a.value > b.value ? 1 : -1));

			const { apiRoutes, tailoredModelsIds } = getApiRoutesAndTailoredModels(
				apiRoutesItems.map((item) => item.key),
				tgStore.tgModelStore.modelsList.map((model) => model.id),
			);

			await accountStore.loadAllUsageData(
				convertDateToUtc(selectedDateRange[0]).toISOString(),
				convertDateToUtc(selectedDateRange[1]).toISOString(),
				apiRoutes,
				defaultUsageTypesDropDownItems.map((item) => item.key),
				tailoredModelsIds,
			);

			const allUsageData = accountStore.allUsageData;
			const allRoutesInDate: string[] = [];
			const allTGModelsInDate: string[] = [];
			if (allUsageData.length > 0) {
				Object.keys(allUsageData[0])
					.filter(([key, value]) => key !== "name" || parseInt(value) !== 0)
					.forEach((key) => {
						const routeName: string | undefined = ROUTES_MAPPER[key as keyof typeof ROUTES_MAPPER];
						if (routeName) {
							allRoutesInDate.push(routeName);
						} else {
							const keyId = key.split("/").pop();
							const modelName = tgStore.tgModelStore.modelsList.find(
								(model: any) => model.id?.toString() === keyId,
							)?.name;
							if (modelName) {
								allTGModelsInDate.push(modelName);
							}
						}
					});
			}
			allTGModelsInDate.push("placeholder");
			allRoutesInDate.push("placeholder");

			const usedAccessMethodsSet = new Set(
				accountStore.allUsageData.map((entry) => entry["access_method"]?.toLowerCase()).filter(Boolean),
			);
			const usageTypeBaseItems = [
				{ key: selectAllLabel, value: selectAllLabel },
				...defaultUsageTypesDropDownItems,
			];
			const usageTypeFiltered = usageTypeBaseItems.filter(
				(item) =>
					item.key === selectAllLabel ||
					usedAccessMethodsSet.has(ACCESS_METHOD_MAPPER[item.key.toLowerCase()]),
			);
			const usageTypeSelected = usageTypeFiltered.map((item) => item.key);

			const localSelectedApiRoutes = apiRoutesSelected;
			const localSelectedTailored: number[] = [];
			const localSelectedAccess = usageTypeSelected.filter((ut) => ut !== selectAllLabel);
			filterUsageData(accountStore, localSelectedApiRoutes, localSelectedTailored, localSelectedAccess);

			const modelLookup = createModelLookup({ models: tgStore.tgModelStore.modelsList });
			const computedUsageData: IUsageData[] = convertUsageData({
				usageData: accountStore.usageData,
				modelLookup,
			});
			filterTabularData(accountStore, computedUsageData);
			const { data, header } = computeTableData(accountStore, tgStore.tgModelStore, t);

			setFeaturesDropDownItems(apiRoutesItems);
			setFilteredFeaturesDropDownItems(apiRoutesItems);
			setSelectedFeatures(apiRoutesSelected);
			setTailoredModelsDropDownItems(tgModelsItems);
			setFilteredTailoredModelsDropDownItems(tgModelsItems);
			setActiveFeatures(allRoutesInDate);
			setActiveTailoredModels(allTGModelsInDate);
			setAccessMethodsDropDownItems(usageTypeFiltered);
			setSelectedAccessMethods(usageTypeSelected);
			setUsageData(computedUsageData);
			setTableData(data);
			setTableHeader(header);
			filterUsageSummary(accountStore);
			setIsLoading(false);
		};
		loadAllUsageData();
	}, [selectedDateRange]);

	const handleSelectDateRange = (range: Date[]) => {
		setSelectedRangeDate(range);
		if (range && range.length === 2 && range[0] instanceof Date && range[1] instanceof Date) {
			setDateRange([range[0], range[1]]);
		}
		
		const dateRangeString = `${range[0].toISOString().split('T')[0]} to ${range[1].toISOString().split('T')[0]}`;
		
		trackEvent(POSTHOG_EVENTS.USAGE_VIEWED, {
			date_range: dateRangeString,
			send_instantly: true,
		});
	};

	const onApiRoutesSearchChange = (value: string) => {
		setFeaturesSearchTerm(value);
		if (value && value.length > 0) {
			const filteredApiRoutes = featuresDropDownItems.filter((item) =>
				item.value.toLowerCase().includes(value.toLowerCase()),
			);
			setFilteredFeaturesDropDownItems(filteredApiRoutes);
		} else {
			setFilteredFeaturesDropDownItems(featuresDropDownItems);
		}
	};

	const handleTGModelsSearchChange = (value: string) => {
		setTgModelSearchTerm(value);
		if (value && value.length > 0) {
			const filteredModels = tailoredModelsDropDownItems.filter((model) =>
				model.key.toLowerCase().includes(value.toLowerCase()),
			);
			setFilteredTailoredModelsDropDownItems(filteredModels);
		} else {
			setFilteredTailoredModelsDropDownItems(tailoredModelsDropDownItems);
		}
	};

	const handleAPIRouteSelectionChange = (value: string[]) => {
		if ((value as any).includes(undefined)) return;

		const usageData = accountStore.usageData[0] || {};
		const keys = ["text-to-image/tailored", "text-to-vector/tailored"];
		const idsToBeDeleted: number[] = [];

		keys.forEach((key) => {
			if (value.includes(key)) {
				selectedTailoredModels.forEach((id) => {
					if (usageData.hasOwnProperty(`${key}/${id}`)) {
						idsToBeDeleted.push(id);
					}
				});
			}
		});

		const updatedSelectedIds = selectedTailoredModels.filter((id) => !idsToBeDeleted.includes(id));
		setSelectedTailoredModels(updatedSelectedIds);
		handleSelectApiRoutes({
			value,
			t,
			filteredApiRoutesDropDownItems: filteredFeaturesDropDownItems,
			selectedApiRoute: selectedFeatures,
			setSelectedApiRoute: setSelectedFeatures,
			activeApiRoutes: activeFeatures,
		});
	};

	const handleUsageTypeSelectionChange = (value: string[]) => {
		handleSelectUsageType({
			value,
			t,
			filteredUsageTypeDropDownItems: accessMethodsDropDownItems,
			selectedUsageType: selectedAccessMethods,
			setSelectedUsageType: setSelectedAccessMethods,
		});
	};

	const handleTailoredModelsChange = (value: number[]) => {
		if (!value || (value as any).includes(undefined)) return;

		const usageData = accountStore.usageData[0] || {};
		const allUsageData = accountStore.allUsageData[0] || {};
		const keys = ["text-to-image/tailored", "text-to-vector/tailored"];
		const routesToBeDeleted: string[] = [];

		value.forEach((id) => {
			keys.forEach((key) => {
				if (
					usageData.hasOwnProperty(key) &&
					allUsageData.hasOwnProperty(`${key}/${id}`) &&
					!routesToBeDeleted.includes(key)
				) {
					routesToBeDeleted.push(key);
				}
			});
		});

		if (routesToBeDeleted.length) {
			handleSelectApiRoutes({
				value: selectedFeatures.filter((route) => !routesToBeDeleted.includes(route)),
				t,
				filteredApiRoutesDropDownItems: filteredFeaturesDropDownItems,
				selectedApiRoute: selectedFeatures,
				setSelectedApiRoute: setSelectedFeatures,
				activeApiRoutes: activeFeatures,
			});
		}
		setSelectedTailoredModels(value);
	};

	const isLoadingData = () => {
		return (
			isLoading ||
			accountStore.isLoading ||
			accountStore.isLoadingSummaryReport ||
			tgStore.tgModelStore.loadingModelsList ||
			appStore.isLoading ||
			accountStore.isLoadingAllUsageData
		);
	};

	const handleResetToDefault = () => {
		handleSelectDateRange(getTodayDateRange());
	};

	useEffect(() => {
		if (!authStore.isLoadingOrgSubscriptions && !isLoading && selectedDateRange.length === 2) {
			const currentDateRange = `${selectedDateRange[0].toISOString().split('T')[0]} to ${selectedDateRange[1].toISOString().split('T')[0]}`;
			
			trackEvent(POSTHOG_EVENTS.USAGE_VIEWED, {
				date_range: currentDateRange,
				send_instantly: true,
			});
		}
	}, [authStore.isLoadingOrgSubscriptions, isLoading, selectedDateRange]);

	return (
		<Box className={styles.container}>
			<Box className={styles.summary}>
				<Box className={styles.column}>
					<Typography
						className={clsx({
							[styles.value]: true,
							[styles.firstColumn]: true,
						})}
					>
						<Typography className={styles.title}>{t("totalCalls")}</Typography>

						<Box className={styles.totalUsageWrapper}>
							<Typography className={styles.totalUsage}>
								{accountStore.usageSummary.totalUsage?.toLocaleString()}
							</Typography>{" "}
							{isLoadingData() && (
								<Box className={classes.circleLoader}>
									<CircularProgress color="inherit" size={16} />
								</Box>
							)}
						</Box>
					</Typography>

					<Typography className={clsx(styles.value, styles.hide)}>
						<Typography className={styles.title}>{t("averageMonthlyCalls")}</Typography>
						{isLoadingData() ? (
							<Box className={classes.circleLoader}>
								<CircularProgress color="inherit" size={20} />
							</Box>
						) : (
							<>{accountStore.usageSummary.monthlyUsage?.toLocaleString()}</>
						)}
					</Typography>
				</Box>
				<Box className={styles.headerActions}>
					<BriaButton
						onClick={handleResetToDefault}
						className={styles.button}
						buttonType="textMedium"
						disabled={isLoadingData()}
					>
						{t("resetToDefault")}
					</BriaButton>
					<BriaButton
						onClick={() =>
							handleDownloadAsCsvClick({ accountStore, tgModelStore: tgStore.tgModelStore, t })
						}
						className={styles.button}
						buttonType="secondaryMedium"
						disabled={isLoadingData()}
					>
						{t("downloadAsCSV")}
					</BriaButton>
				</Box>
			</Box>

			<Box className={styles.filtersContainer}>
				<Box className={styles.filters}>
					<Box className={styles.dropdowns}>
						<BriaDropDown
							value={selectedAccessMethods}
							items={accessMethodsDropDownItems}
							height={DROPDOWN_HEIGHT}
							maxHeight={DROPDOWN_HEIGHT}
							multiple={true}
							width={DROPDOWN_WIDTH}
							menuMaxHeight={DROPDOWN_MAX_HEIGHT}
							placeholder={t("accessMethods")}
							loading={isLoadingData()}
							disabled={isLoadingData()}
							selectMultipleCheckbox={true}
							onChange={(e) => handleUsageTypeSelectionChange(e.target.value)}
							renderValue={(selected) => {
								if (selected.length === 0) {
									return <>{t("accessMethods")}</>;
								}
								return t("accessMethods");
							}}
						/>
						<BriaDropDown
							value={selectedFeatures}
							items={filteredFeaturesDropDownItems}
							height={DROPDOWN_HEIGHT}
							maxHeight={DROPDOWN_HEIGHT}
							multiple={true}
							width={DROPDOWN_WIDTH}
							menuMaxHeight={DROPDOWN_MAX_HEIGHT}
							placeholder={t("features")}
							SearchPlaceholder={t("features")}
							loading={tgStore.tgModelStore.loadingModelsList}
							disabled={tgStore.tgModelStore.loadingModelsList || accountStore.isLoadingAllUsageData}
							selectMultipleCheckbox={true}
							onChange={(e) => handleAPIRouteSelectionChange(e.target.value)}
							renderValue={(selected) => {
								if (selected.length === 0 || activeFeatures.length === 0) {
									return <>{t("features")}</>;
								}
								const filteredLength = selected.filter((sel: string) =>
									activeFeatures.some((act) => act === sel),
								).length;
								return `${filteredLength} ${t("features")}`;
							}}
							activeItems={activeFeatures}
							onSearchChange={(e) => onApiRoutesSearchChange(e.target.value)}
							searchable={true}
							searchIcon={true}
							searchValue={featuresSearchTerm}
						/>
						<BriaDropDown
							value={selectedUsagePeriod}
							onChange={(e) => setSelectedUsagePeriod(e.target.value as UsagePeriod)}
							items={usagePeriodDropDownItems}
							height={DROPDOWN_HEIGHT}
							disabled={isLoadingData()}
							className={styles.hide}
						/>
						<BriaDropDown
							value={selectedTailoredModels}
							items={filteredTailoredModelsDropDownItems}
							activeItems={activeTailoredModels}
							loading={tgStore.tgModelStore.loadingModelsList}
							disabled={tgStore.tgModelStore.loadingModelsList || accountStore.isLoadingAllUsageData}
							selectMultipleCheckbox={true}
							multiple={true}
							height={DROPDOWN_HEIGHT}
							maxHeight={DROPDOWN_HEIGHT}
							menuMaxHeight={DROPDOWN_MAX_HEIGHT}
							width={TG_MODEL_DROPDOWN_WIDTH}
							searchValue={tgModelSearchTerm}
							searchIcon={true}
							searchable={true}
							SearchPlaceholder={t("tailoredGenerationModels")}
							onSearchChange={(e) => handleTGModelsSearchChange(e.target.value)}
							placeholder={t("tailoredGenerationModels")}
							onChange={(e) => handleTailoredModelsChange(e.target.value)}
							renderValue={(selected) => {
								if (selected.length) {
									return `${selected.length} ${t("tailoredGenerationModelsSelected")}`;
								}
								return <>{t("tailoredGenerationModels")}</>;
							}}
						/>
					</Box>
					<Box className={styles.dateRangePicker}>
						<CustomDateRangePicker
							loading={isLoadingData()}
							placement="autoVerticalEnd"
							value={dateRange}
							hoverRange={
								selectedUsagePeriod === UsagePeriod.Daily ? handleHoverRange : selectedUsagePeriod
							}
							onChange={handleSelectDateRange}
							cleanable={false}
						/>
					</Box>
				</Box>
			</Box>
			<Box className={styles.chartArea}>
				<ResponsiveContainer>
					<CustomLineChart
						data={usageData.length === 0 ? LineChartDefaultValues : usageData}
						total={usageData.length}
						colors={LineChartColors}
					/>
				</ResponsiveContainer>
			</Box>
			{accountStore.usageData.length != 0 && (
				<Box className={styles.tableArea}>
					<TableLayout
						hideSecondaryButton={true}
						headerArray={tableHeader}
						tableData={tableData}
						enableSearch={false}
						loading={isLoadingData()}
					/>
				</Box>
			)}
		</Box>
	);
};

const ObservedComponent = observer(UsageAndBilling);
export default ObservedComponent;
